import { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./SocialHive.css";
import SHSidebar from "./components/SHSidebar/SHSidebar";
import SocialHiveHeader from "./components/SocialHiveHeader/SocialHiveHeader";
import SocialHiveChat from "./views/SocialHiveChat/SocialHiveChat";
import SocialHiveSettings from "./views/SocialHiveSettings/SocialHiveSettings";
import { useLanguage } from "../../LanguageProvider";
import SocialHiveEvents from "./views/SocialHiveEvents/SocialHiveEvents";
import SocialHiveAnnouncements from "./views/SocialHiveAnnouncements/SocialHiveAnnouncements";
import sampleImg from "../../assets/eventCardSample.png";
import SocialHiveDocuments from "./views/SocialHiveDocuments/SocialHiveDocuments";
import SocialHiveGallery from "./views/SocialHiveGallery/SocialHiveGallery";
import { GeneralContext } from "../../GeneralProvider";
import SocialHiveDiscover from "./views/SocialHiveDiscover/SocialHiveDiscover";
import io from 'socket.io-client';
import Services from "../../services/Services";
import MultiRoomVoiceChat from "../MultiRoomVoiceChat";
import VoiceRoom from "../VoiceRoom";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const NOTIFICATION_SOUND_URL = "https://assets.lodos.io/dm-receive-message.mp3";

export default function SocialHive() {
  const socketRef = useRef();
  const notificationAudio = useRef(new Audio(NOTIFICATION_SOUND_URL));
  const { getTranslation } = useLanguage();
  const defaultChannel = { channelID: "0", title: getTranslation("directMessages") };
  const { getMyChannels, myChannels, getChannelRooms, channelRooms, userId, handleJoinRoom, shChannelUri, dmRoomUri, setDmRoomUri, setOrderedRooms, orderedRooms, eventIDUri, setCreateProjectLoading, resetShAnalytics } = useContext(GeneralContext);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [activeChannel, setActiveChannel] = useState(defaultChannel);
  const [activeRoom, setActiveRoom] = useState("");
  const [activeView, setActiveView] = useState("chat");
  const [selectedSearchChannel, setSelectedSearchChannel] = useState("");
  const [isSearchedChannelJoined, setIsSearchedChannelJoined] = useState(false);
  const [dmRefreshFlag, setDmRefreshFlag] = useState(false);
  const [dmRoomsList, setDmRoomsList] = useState([]);

  const [roomsWithUnread, setRoomsWithUnread] = useState([]);
  const [activeSettingsMenu, setActiveSettingsMenu] = useState("authorizations");
  useEffect(() => {
    resetShAnalytics();
    return () => resetShAnalytics();
  }, []);

  useEffect(() => {
    getMyChannels();
  }, []);

  useEffect(() => {
    // Sesin önceden yüklenmesini sağlıyoruz
    notificationAudio.current.preload = "auto";
    notificationAudio.current.load();
  }, []);

  useEffect(() => {
    if (activeChannel.channelID !== "0") {
      const foundChannel = myChannels.find(channel => channel.channelID === activeChannel.channelID);
      handleChannelChange(foundChannel, true)
    }
  }, [myChannels]);

  const handleChannelChange = (channel, reset = false) => {
    if (!reset) {
      setActiveView("");
      setActiveRoom("");
    }

    if (channel) {
      const token = localStorage.getItem("token");

      getChannelRooms(channel.channelID).then((rooms) => {
        const roomOrder = channel?.settings?.roomOrder ?? [];

        const sortedRooms = roomOrder.length > 0
          ? [...rooms].sort((a, b) =>
            roomOrder.indexOf(a.roomID) - roomOrder.indexOf(b.roomID)
          )
          : rooms;

        setOrderedRooms(sortedRooms);

        // 🆕 Odaları kanal objesine dahil et
        const enrichedChannel = {
          ...channel,
          rooms: sortedRooms
        };

        setActiveChannel(enrichedChannel); // ✅ selectedChannel'a rooms gider
      });

    } else {
      if (!shChannelUri) {
        setActiveChannel(defaultChannel);
        setSelectedSearchChannel("");
      }
    }
    setSelectedAnnouncement(null);
  };

  useEffect(() => {

    if (eventIDUri) {
      const token = localStorage.getItem("token");

      Services.SocialHiveGetEventDetails(token, eventIDUri)
        .then((res) => {
          if (res.data.status) {
            setSelectedEvent(res.data.result)
            setActiveView("events")
            const foundChannel = myChannels?.find(channel => channel.channelID === res.data.result.channelID);
            handleChannelChange(foundChannel, true)
          } else {
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [myChannels]);

  useEffect(() => {
    const id = activeChannel.channelID;
    if (!id || id === "0" || id === "discover") return;

    const token = localStorage.getItem("token");
    setCreateProjectLoading(true)
    getChannelRooms(id).then((rooms) => {
      const roomOrder = activeChannel?.settings?.roomOrder ?? [];
      setCreateProjectLoading(false)
      const sorted = roomOrder.length > 0
        ? [...rooms].sort((a, b) =>
          roomOrder.indexOf(a.roomID) - roomOrder.indexOf(b.roomID)
        )
        : rooms;

      setOrderedRooms(sorted);
    });
  }, [activeChannel.channelID]);

  const fetchMyDMList = () => {
    if (activeView !== "chat") {
      setCreateProjectLoading(true)
    }
    const token = localStorage.getItem("token");
    Services.SocialHiveGetDMRoomList(token)
      .then(res => {
        setCreateProjectLoading(false)
        if (res.data.status) {
          setDmRoomsList(res.data.result);
        } else {
          setDmRoomsList([]);
        }
      })
      .catch(() => setDmRoomsList([]));
  };
  // Aktif kanal “0” (DM) olduğunda veya dmRefreshFlag değiştiğinde listeyi yenile
  useEffect(() => {
    if (activeChannel.channelID === "0") {
      fetchMyDMList();
    }
  }, [activeChannel.channelID, dmRefreshFlag]);

  useEffect(() => {
    if (!dmRoomUri) return;

    // 1) DM kanalına geç
    setActiveChannel({ channelID: "0", title: getTranslation("directMessages") });

    // 2) DM odaları listesi geldiğinde ara
    const dmRoom = dmRoomsList.find(r => r.roomID === dmRoomUri);
    if (dmRoom) {
      // handleJoinRoom("0", dmRoom.roomID);
      setActiveView("chat");
      setActiveRoom(dmRoom);
      setDmRoomUri(""); // bir daha tetiklemesin
    }
  }, [dmRoomUri, dmRoomsList]);

  useEffect(() => {
    if (activeChannel.channelID !== "0" && activeChannel.channelID !== "discover" && activeView !== "events") {
      const rooms = channelRooms.filter(r => r.channelID === activeChannel.channelID);

      if (rooms.length === 1 && activeView !== "settings") {
        const onlyRoom = rooms[0];
        // Odaya katıl
        handleJoinRoom(activeChannel.channelID, onlyRoom.roomID);
        // Chat görünümüne geç ve odayı aktif et
        setActiveView("chat");
        setActiveRoom(onlyRoom);
        socketRef.current.emit('joinRoom', onlyRoom.roomID);
      }
    }
  }, [channelRooms, activeChannel.channelID]);

  useEffect(() => {
    if (!shChannelUri) return;

    // 1) Discover sekmesine geç
    setActiveView("discover");
    setActiveChannel({ channelID: "discover", title: getTranslation("discover") });

    // 2) URI'ye göre kanal bilgisini al
    const token = localStorage.getItem("token");
    Services.SocialHiveChannelSearch(token, shChannelUri)
      .then(res => {
        if (res.data.status && res.data.result.length > 0) {
          const channel = res.data.result[0];
          setSelectedSearchChannel(channel);

          // 3) Kullanıcının bu kanala zaten kayıtlı olup olmadığını kontrol et
          const joined = myChannels.some(c => c.channelID === channel.channelID);
          setIsSearchedChannelJoined(joined);
        }
      })
      .catch(err => console.error("URI kanal verisi alınamadı:", err));
  }, [shChannelUri, myChannels]);


  const handleTabChange = (newActiveView) => {
    setSelectedEvent(null);
    setSelectedAnnouncement(null);
    setActiveView(newActiveView);
  };

  const categories = useMemo(() => [
    {
      name: getTranslation("general"),
      rooms: orderedRooms,
    },
  ], [orderedRooms, getTranslation]);

  useEffect(() => {
    socketRef.current = io(SOCKET_URL);
    socketRef.current.emit('joinRoom', "DM" + userId);

    socketRef.current.on('message', (msg) => {
      const isDifferentRoom = activeRoom.roomID !== msg.roomID;
      const isTabHidden = document.hidden;
      setDmRefreshFlag(prev => !prev);
      let unreadRooms = roomsWithUnread
      if (isDifferentRoom) {
        unreadRooms.push(msg.roomID)
        setRoomsWithUnread(unreadRooms)
      }

      if (isDifferentRoom || isTabHidden) {
        notificationAudio.current
          .play()
          .catch(err => console.warn("Ses çalınamadı:", err));
      }
    });

    return () => {
      socketRef.current.disconnect();
    };
  }, [userId, activeRoom.roomID]);

  useEffect(() => {
    if (shChannelUri) {
      setActiveView("discover")
      setActiveChannel({ channelID: "discover", title: getTranslation("discover") })
    }
  }, []);

  return (
    <div className="sh-container">
      <SHSidebar
        channels={myChannels}
        categories={categories}
        onSettingsClick={() => setActiveView("settings")}
        activeView={activeView}
        onDiscoverClick={() => setActiveView("discover")}
        onRoomClick={() => setActiveView("chat")}
        activeChannel={activeChannel}
        setActiveChannel={handleChannelChange}
        activeRoom={activeRoom}
        setActiveRoom={setActiveRoom}
        getChannelRooms={(id) => getChannelRooms(id)}
        setSelectedSearchChannel={setSelectedSearchChannel}
        selectedSearchChannel={selectedSearchChannel}
        setIsSearchedChannelJoined={setIsSearchedChannelJoined}
        dmRefreshFlag={dmRefreshFlag}
        roomsWithUnreadMessage={roomsWithUnread}
        setRoomsWithUnread={setRoomsWithUnread}
        orderedRooms={orderedRooms}
        setOrderedRooms={setOrderedRooms}
        getMyChannels={getMyChannels}
        isSettings={activeView === "settings"}
        activeSettingsMenu={activeSettingsMenu}
        setActiveSettingsMenu={setActiveSettingsMenu}
      />
      <div
        style={{
          height: "100%",
          width: "100%",
          maxWidth: "calc(100% - 228px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SocialHiveHeader
          activeView={activeView}
          setActiveView={handleTabChange}
          activeRoom={activeChannel.channelID === "0" && activeRoom}
          activeChannel={activeChannel}
        />
        {(activeView === "chat" && activeRoom) && <SocialHiveChat activeRoom={activeRoom} setActiveRoom={setActiveRoom} setActiveChannel={setActiveChannel} selectedChannel={activeChannel} />}
        {(!activeView && !activeRoom && activeChannel.channelID !== "0") &&
          <div className="room-list-container">
            {categories.map((category, idx) => (
              <div key={idx} className="room-category">
                <div className="room-items">
                  {category.rooms.map((room) => (
                    <div
                      key={room.roomID}
                      className="room-item"
                      onClick={() => {
                        handleJoinRoom(activeChannel.channelID, room.roomID);
                        setActiveRoom(room);
                        setActiveView("chat");
                      }}
                    >
                      {room.title}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        }

        {(activeView === "discover" && selectedSearchChannel) && <SocialHiveDiscover setSelectedSearchChannel={setSelectedSearchChannel} isJoined={isSearchedChannelJoined} selectedSearchChannel={selectedSearchChannel} handleChannelChange={handleChannelChange} setActiveView={setActiveView} />}
        {activeView === "settings" && <SocialHiveSettings
          selectedChannel={activeChannel}
          handleChannelChange={handleChannelChange}
          activeMenu={activeSettingsMenu}
        />}
        {activeView === "events" && (
          <SocialHiveEvents
            selectedEvent={selectedEvent}
            setSelectedEvent={setSelectedEvent}
            activeChannel={activeChannel}
            handleChannelChange={handleChannelChange}
          />
        )}
        {activeView === "announcements" && (
          <SocialHiveAnnouncements
            selectedAnnouncement={selectedAnnouncement}
            setSelectedAnnouncement={setSelectedAnnouncement}
            activeChannel={activeChannel}
          />
        )}
        {/* {activeView === "documents" && <MultiRoomVoiceChat wsBaseUrl={"wss://webrtc-01.lodos.io/ws/"} clientId={userId}/>} */}
        {activeView === "documents" && <SocialHiveDocuments />}
        {activeView === "gallery" && <SocialHiveGallery activeChannel={activeChannel} />}
      </div>
    </div>
  );
}
